import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

// component imports
import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import {
    RoundListNavItem,
    SeasonNavItem,
    CompNavItem
} from 'common/react/components/competition-nav/nav-items';

//store imports
import { navSelector } from 'store/modules/competition-nav/selectors/nav-selector';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { FilterButton } from 'common/react/components/FilterButton';
import { getTranslation } from 'common/react/utils/translations';
import { initialiseNav } from 'common/store/modules/competition-nav/thunks';
import { useGetUrlParams } from 'common/react/components/competition-nav/hooks/use-get-url-params';
import { useSyncUrlParams } from 'common/react/hooks/use-sync-url-params';
import { useHasChangedFromDefaults } from 'common/react/components/competition-nav/hooks/use-has-changed-from-defaults';
import { useResetToDefaults } from 'common/react/components/competition-nav/hooks/use-reset-to-defaults';

export const VideoHub = ({ competitionId, seasonId, title }) => {
    const dispatch = useDispatch();
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);
    const [resetFilterBtnStyles, setResetFilterBtnStyles] = useState(false);
    const windowSize = useWindowSizeV2();

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
    }, [windowSize.width]);

    const params = useGetUrlParams();

    const initialValues = {
        competitionId: params.competition || competitionId
    };
    const competitionNavRef = useRef();

    const nav = useSelector(navSelector);
    const options = useCompetitionNavigationOptions(nav);

    useEffect(() => {
        dispatch(
            initialiseNav({
                initialValues: initialValues
            })
        );
    }, [dispatch]);

    useSyncUrlParams({
        selector: navSelector,
        mapToParams: (items) => ({
            comp: items.competition.id
        })
    });

    const { competitions, seasons } = options;

    const { hasChangedFromDefaults, isSeasonChanged } =
        useHasChangedFromDefaults({
            state: { nav: nav },
            initialValues: {
                competitionId,
                seasonId
            }
        });

    const resetToDefaults = useResetToDefaults({
        initialValues: {
            competitionId,
            seasonId
        }
    });

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: nav,
        options,
        filtersFields: ['competition', 'season', 'round'],
        filtersRef: competitionNavRef
    });

    return (
        <div
            className="competition-nav filter-dropdown rounded-roundlist-wrapper"
            ref={competitionNavRef}
        >
            <StatsSection.Main>
                <StatsSection.Heading title={title} />
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <CompNavItem
                            competitions={competitions}
                            classes="video-hub-nav__comp-item"
                        />
                        {!showResponsiveItems ? (
                            <SeasonNavItem seasons={seasons} hideLabel />
                        ) : null}
                        {!showResponsiveItems && hasChangedFromDefaults ? (
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                        {showResponsiveItems ? (
                            <FilterButton
                                classes="fixtures-nav__filter-button"
                                onChange={() =>
                                    setAreFilterOptionsVisible((prev) => !prev)
                                }
                                areFilterOptionsVisible={
                                    areFilterOptionsVisible
                                }
                                resetFilterBtnStyles={resetFilterBtnStyles}
                                setResetFilterBtnStyles={
                                    setResetFilterBtnStyles
                                }
                                showFilterLabel={false}
                                showActiveCircle
                                isFilterSelectedCustom={() => isSeasonChanged}
                            />
                        ) : null}
                        {showResponsiveItems ? (
                            <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                                <SettingsButton />
                            </StatsSection.SettingsButton>
                        ) : null}
                    </StatsSection.NavItems>
                    <StatsSection.SettingsButton>
                        <SettingsButton />
                    </StatsSection.SettingsButton>
                    <StatsSection.RoundButtons>
                        <RoundListNavItem
                            classes={'competition-nav__round-filter-wrapper'}
                        />
                    </StatsSection.RoundButtons>
                    {showResponsiveItems && areFilterOptionsVisible ? (
                        <StatsSection.NavFilterItems
                            initialValues={initialValues}
                            hideReset={!hasChangedFromDefaults}
                            onReset={resetToDefaults}
                        >
                            <SeasonNavItem seasons={seasons} hideLabel />
                        </StatsSection.NavFilterItems>
                    ) : null}
                </StatsSection.Nav>
            </StatsSection.Main>
        </div>
    );
};

VideoHub.propTypes = {
    seasonPid: PropTypes.string,
    competitionId: PropTypes.string,
    seasonId: PropTypes.string,
    round: PropTypes.string,
    title: PropTypes.string
};
